/**
 * loaderConfig.ts — Global configuration for Draco and KTX2/Basis loaders.
 *
 * Draco:  Enables loading Draco-compressed GLB/glTF files.
 * KTX2:   Enables loading KTX2/Basis Universal compressed textures.
 *
 * Uses CDN-hosted decoder/transcoder WASM files so we don't need to
 * copy them into public/.
 */
import { useGLTF } from '@react-three/drei';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import * as THREE from 'three';

// CDN paths for decoder/transcoder WASM files
const DRACO_DECODER_PATH = 'https://www.gstatic.com/draco/versioned/decoders/1.5.7/';
const BASIS_TRANSCODER_PATH = 'https://cdn.jsdelivr.net/npm/three@0.178.0/examples/jsm/libs/basis/';

// ---- Draco ----

/**
 * Shared DRACOLoader instance.  Re-used by both drei's useGLTF (via
 * setDecoderPath) and any raw GLTFLoader that needs Draco support.
 */
let _dracoLoader: DRACOLoader | null = null;

export function getDRACOLoader(): DRACOLoader {
  if (!_dracoLoader) {
    _dracoLoader = new DRACOLoader();
    _dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
    _dracoLoader.setDecoderConfig({ type: 'js' }); // works everywhere; use 'wasm' if you prefer
  }
  return _dracoLoader;
}

// ---- KTX2 ----

/**
 * Shared KTX2Loader instance.  Requires a WebGLRenderer to detect GPU
 * capabilities, so call `initKTX2Loader(renderer)` once the Canvas is ready.
 */
let _ktx2Loader: KTX2Loader | null = null;

export function getKTX2Loader(): KTX2Loader | null {
  return _ktx2Loader;
}

export function initKTX2Loader(renderer: THREE.WebGLRenderer): KTX2Loader {
  if (!_ktx2Loader) {
    _ktx2Loader = new KTX2Loader();
    _ktx2Loader.setTranscoderPath(BASIS_TRANSCODER_PATH);
    _ktx2Loader.detectSupport(renderer);
    console.log('[loaderConfig] KTX2Loader initialized with Basis transcoder');
  }
  return _ktx2Loader;
}

// ---- Global setup (run once at import time) ----

// Tell drei's useGLTF to use the Draco decoder CDN.
// This means every <Model> / useGLTF() call can transparently load
// Draco-compressed meshes without per-component configuration.
useGLTF.setDecoderPath(DRACO_DECODER_PATH);
console.log('[loaderConfig] Draco decoder path set for useGLTF:', DRACO_DECODER_PATH);

// ---- Meshopt ----
// Export the MeshoptDecoder for use with GLTFLoader.setMeshoptDecoder()
// This enables loading meshopt-compressed models (EXT_meshopt_compression)
export { MeshoptDecoder };
console.log('[loaderConfig] MeshoptDecoder available for meshopt-compressed models');
